import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Card,
  CardContent
} from '@mui/material';
import { Add, Delete, Edit, Save, Close } from '@mui/icons-material';
import ApiContext from 'contexts/ApiContext';
import useApi from 'hooks/useApi';

// ============================================================================
// Types
// ============================================================================

type RuleType = 'STEP_TIMER' | 'INTER_STEP_TRACKING' | 'GEO_FENCE' | 'MULTI_USER_PROXIMITY' | 'DRIVER_STOPPED' | 'LOCATION_GATE';
type AnchorType = 'STATIC' | 'STEP_LOCATION' | 'STEP_FIELD';
type Severity = 'INFO' | 'WARNING' | 'CRITICAL';
type ProximityMode = 'OUT_OF_PROXIMITY' | 'IN_PROXIMITY';
type GeoFenceDirection = 'ENTER' | 'EXIT' | 'RING_DWELL';

const PROXIMITY_MODE_OPTIONS: { value: ProximityMode; label: string; description: string }[] = [
  { value: 'OUT_OF_PROXIMITY', label: 'Users are too far apart', description: 'Fire when any pair of active users is more than the threshold apart.' },
  { value: 'IN_PROXIMITY', label: 'Users are within threshold (together)', description: 'Fire when every active user is within the threshold of each other.' }
];

interface NotificationConfig {
  users: string[];
  roles: string[];
  channels: string[];
  severity: Severity;
  transactionOnly?: boolean;
  // Per-rule alert wording with {{variable}} placeholders. Blank = the
  // built-in text for that rule type. See ALERT_TEMPLATE_VARIABLES.
  titleTemplate?: string;
  messageTemplate?: string;
}

interface MonitoringRule {
  uid?: string;
  name: string;
  enabled: boolean;
  ruleType: RuleType;
  startStepUid?: string;
  endStepUid?: string;
  maxDurationMinutes?: number;
  intervalMinutes?: number;
  geoFenceAnchor?: {
    type: AnchorType;
    coordinates?: [number, number];
    label?: string;
    anchorStepUid?: string;
    anchorFieldUid?: string;
  };
  geoFenceRadiusMeters?: number;
  geoFenceTrackedStepUid?: string;
  geoFenceDirection?: GeoFenceDirection;
  geoFenceFireOnce?: boolean;
  /** RING_DWELL only — outer edge of the ring; the radius above is the inner edge. */
  geoFenceOuterRadiusMeters?: number;
  /** RING_DWELL only — minutes the driver must stay in the ring before firing. */
  geoFenceDwellMinutes?: number;
  proximityRadiusMeters?: number;
  proximityMinUsers?: number;
  proximityMode?: ProximityMode;
  stopRadiusMeters?: number;
  stopDurationMinutes?: number;
  stopAnchorMode?: 'SELF' | 'FIXED';
  stopAnchor?: {
    type: AnchorType;
    coordinates?: [number, number];
    label?: string;
    anchorStepUid?: string;
    anchorFieldUid?: string;
  };
  gateStepUid?: string;
  gateAnchor?: {
    type: AnchorType;
    coordinates?: [number, number];
    label?: string;
    anchorStepUid?: string;
    anchorFieldUid?: string;
  };
  gateRadiusMeters?: number;
  cooldownMinutes: number;
  notification: NotificationConfig;
}

interface MonitoringSettingsProps {
  teamUid: string;
  clientUID?: string;
}

const RULE_TYPE_OPTIONS: { value: RuleType; label: string; description: string }[] = [
  { value: 'STEP_TIMER', label: 'Step Timer', description: 'Alert if step B not completed within N minutes of step A.' },
  {
    value: 'INTER_STEP_TRACKING',
    label: 'Inter-Step Tracking',
    description: 'Periodic heartbeat alert during the window between two steps.'
  },
  {
    value: 'GEO_FENCE',
    label: 'Geo-Fence',
    description: 'Alert if a user drifts more than N meters from an anchor while a tracked step is live.'
  },
  {
    value: 'MULTI_USER_PROXIMITY',
    label: 'Multi-User Proximity',
    description: 'Alert if active users on the same transaction are more than N meters apart.'
  },
  {
    value: 'DRIVER_STOPPED',
    label: 'Driver Stopped',
    description: 'Alert if a driver stays within N meters for M minutes — anywhere, or near a specific point.'
  },
  {
    value: 'LOCATION_GATE',
    label: 'Location Lock (step completion)',
    description: 'Block completing a step until the driver is within N meters of a destination (from a step field or a fixed point).'
  }
];

const CHANNELS = ['EMAIL', 'SMS', 'IN-APP', 'PRIORITY', 'ALERT'];
const SEVERITIES: Severity[] = ['INFO', 'WARNING', 'CRITICAL'];

// ---------------------------------------------------------------------------
// Alert text templates
// ---------------------------------------------------------------------------

interface TemplateVar {
  name: string;
  description: string;
}

// Available to every rule that fires an alert. LOCATION_GATE is excluded — its
// text is a step-refusal shown to the driver, produced outside the alert
// pipeline, where these values aren't in hand.
const COMMON_VARS: TemplateVar[] = [
  { name: 'ruleName', description: 'This rule’s name' },
  { name: 'transactionKey', description: 'Transaction key, e.g. TXN-10428' },
  { name: 'severity', description: 'INFO / WARNING / CRITICAL' },
  { name: 'time', description: 'When the alert fired' },
  { name: 'defaultTitle', description: 'The built-in title — use to extend rather than replace it' },
  { name: 'defaultMessage', description: 'The built-in message — use to extend rather than replace it' }
];

// Rule-specific variables, drawn from the context each rule reports when it fires.
const RULE_VARS: Record<RuleType, TemplateVar[]> = {
  STEP_TIMER: [
    { name: 'stepTitle', description: 'The step that went overdue' },
    { name: 'elapsedMinutes', description: 'Minutes elapsed' },
    { name: 'limitMinutes', description: 'The configured limit' }
  ],
  INTER_STEP_TRACKING: [
    { name: 'startStepTitle', description: 'Step the window started from' },
    { name: 'stepTitle', description: 'Step the window is waiting on' },
    { name: 'elapsedMinutes', description: 'Minutes elapsed since the start step' },
    { name: 'limitMinutes', description: 'The configured max duration' }
  ],
  GEO_FENCE: [
    { name: 'userName', description: 'The driver who triggered it' },
    { name: 'distanceMeters', description: 'Their distance from the anchor' },
    { name: 'radiusMeters', description: 'The configured radius (ENTER / EXIT)' },
    { name: 'anchorLabel', description: 'Label of the anchor point' },
    { name: 'coordinates', description: 'Their current lat, long' },
    { name: 'direction', description: 'ENTER, EXIT or RING_DWELL' },
    { name: 'innerRadiusMeters', description: 'Inner edge of the ring (stays-outside rules)' },
    { name: 'outerRadiusMeters', description: 'Outer edge of the ring (stays-outside rules)' },
    { name: 'minutesInRing', description: 'How long they have been in the ring' },
    { name: 'dwellMinutes', description: 'The configured limit before it fires' }
  ],
  MULTI_USER_PROXIMITY: [
    { name: 'userCount', description: 'How many users are active' },
    { name: 'userNames', description: 'Their names, comma-separated' },
    { name: 'thresholdMeters', description: 'The configured threshold' },
    { name: 'distanceMeters', description: 'Widest measured gap between users' },
    { name: 'proximityMode', description: 'IN_PROXIMITY or OUT_OF_PROXIMITY' }
  ],
  DRIVER_STOPPED: [
    { name: 'userName', description: 'The driver who stopped' },
    { name: 'stoppedMinutes', description: 'How long they have been stationary' },
    { name: 'stopDurationMinutes', description: 'The configured dwell limit' },
    { name: 'stopRadiusMeters', description: 'The configured radius' },
    { name: 'distanceMeters', description: 'Distance from the point (fixed-point mode only)' },
    { name: 'anchorLabel', description: 'Label of the point they stopped near' },
    { name: 'coordinates', description: 'Their current lat, long' }
  ],
  LOCATION_GATE: [
    { name: 'distanceMeters', description: 'How far the driver is from the destination' },
    { name: 'radiusMeters', description: 'How close they must be' },
    { name: 'anchorLabel', description: 'Label of the destination' },
    { name: 'ruleName', description: 'This rule’s name' },
    { name: 'defaultMessage', description: 'The built-in message' }
  ]
};

const templateVarsFor = (ruleType: RuleType): TemplateVar[] =>
  ruleType === 'LOCATION_GATE' ? RULE_VARS.LOCATION_GATE : [...RULE_VARS[ruleType], ...COMMON_VARS];

// Stand-in values so the admin can see the shape of the finished text.
const PREVIEW_SAMPLES: Record<string, string> = {
  ruleName: 'Idle driver check',
  transactionKey: 'TXN-10428',
  severity: 'CRITICAL',
  time: '03/08/2026, 14:22:07',
  defaultTitle: 'Driver stopped',
  defaultMessage: 'Driver stationary for 18 min within 50 m (limit 15 min).',
  userName: 'Ravi Kumar',
  userNames: 'Ravi Kumar, Amit Shah',
  userCount: '3',
  stepTitle: 'Delivery Proof',
  startStepTitle: 'Pickup',
  elapsedMinutes: '42',
  limitMinutes: '30',
  distanceMeters: '820',
  radiusMeters: '200',
  thresholdMeters: '100',
  anchorLabel: 'Warehouse gate',
  coordinates: '19.076, 72.8777',
  direction: 'EXIT',
  innerRadiusMeters: '100',
  outerRadiusMeters: '250',
  minutesInRing: '7',
  dwellMinutes: '5',
  proximityMode: 'OUT_OF_PROXIMITY',
  stoppedMinutes: '18',
  stopDurationMinutes: '15',
  stopRadiusMeters: '50'
};

// Mirrors renderAlertTemplate on the backend: unknown tokens resolve to ''.
const renderPreview = (template: string): string =>
  (template || '').replace(/{{\s*([\w.]+)\s*}}/g, (_m, key: string) => PREVIEW_SAMPLES[key] ?? '');

const emptyNotification = (): NotificationConfig => ({
  users: [],
  roles: [],
  channels: ['IN-APP'],
  severity: 'WARNING',
  transactionOnly: false,
  titleTemplate: '',
  messageTemplate: ''
});

const emptyRule = (ruleType: RuleType = 'STEP_TIMER'): MonitoringRule => ({
  name: '',
  enabled: true,
  ruleType,
  cooldownMinutes: 5,
  notification: emptyNotification(),
  ...(ruleType === 'GEO_FENCE'
    ? {
        geoFenceAnchor: { type: 'STATIC' as AnchorType, coordinates: [0, 0] as [number, number] },
        geoFenceRadiusMeters: 100,
        geoFenceDirection: 'EXIT' as const,
        geoFenceFireOnce: true,
        // Seeded so switching "Trigger when" to the ring mode lands on a valid
        // config instead of an immediately-invalid form.
        geoFenceOuterRadiusMeters: 250,
        geoFenceDwellMinutes: 5
      }
    : {}),
  ...(ruleType === 'MULTI_USER_PROXIMITY'
    ? { proximityRadiusMeters: 50, proximityMinUsers: 2, proximityMode: 'OUT_OF_PROXIMITY' as ProximityMode }
    : {}),
  ...(ruleType === 'DRIVER_STOPPED' ? { stopRadiusMeters: 50, stopDurationMinutes: 5, stopAnchorMode: 'SELF' as const } : {}),
  ...(ruleType === 'LOCATION_GATE' ? { gateAnchor: { type: 'STEP_FIELD' as AnchorType }, gateRadiusMeters: 100 } : {})
});

const safeRule = (raw: any): MonitoringRule => ({
  uid: raw?.uid,
  name: raw?.name || '',
  enabled: raw?.enabled !== false,
  ruleType: raw?.ruleType || 'STEP_TIMER',
  startStepUid: raw?.startStepUid,
  endStepUid: raw?.endStepUid,
  maxDurationMinutes: raw?.maxDurationMinutes,
  intervalMinutes: raw?.intervalMinutes,
  geoFenceAnchor: raw?.geoFenceAnchor,
  geoFenceRadiusMeters: raw?.geoFenceRadiusMeters,
  geoFenceTrackedStepUid: raw?.geoFenceTrackedStepUid,
  geoFenceDirection: raw?.geoFenceDirection || 'EXIT',
  geoFenceFireOnce: raw?.geoFenceFireOnce !== false,
  geoFenceOuterRadiusMeters: raw?.geoFenceOuterRadiusMeters,
  geoFenceDwellMinutes: raw?.geoFenceDwellMinutes,
  proximityRadiusMeters: raw?.proximityRadiusMeters,
  proximityMinUsers: raw?.proximityMinUsers,
  proximityMode:
    raw?.proximityMode === 'IN_PROXIMITY' || raw?.proximityMode === 'OUT_OF_PROXIMITY'
      ? raw.proximityMode
      : raw?.ruleType === 'MULTI_USER_PROXIMITY'
        ? 'OUT_OF_PROXIMITY'
        : undefined,
  stopRadiusMeters: raw?.stopRadiusMeters,
  stopDurationMinutes: raw?.stopDurationMinutes,
  stopAnchorMode: raw?.stopAnchorMode === 'FIXED' ? 'FIXED' : raw?.ruleType === 'DRIVER_STOPPED' ? 'SELF' : undefined,
  stopAnchor: raw?.stopAnchor,
  gateStepUid: raw?.gateStepUid,
  gateAnchor: raw?.gateAnchor,
  gateRadiusMeters: raw?.gateRadiusMeters,
  cooldownMinutes: raw?.cooldownMinutes ?? 5,
  notification: { ...emptyNotification(), ...(raw?.notification || {}) }
});

// ============================================================================
// Component
// ============================================================================

const MonitoringSettings: React.FC<MonitoringSettingsProps> = ({ teamUid, clientUID }) => {
  const apiContext = useContext(ApiContext);
  const { showToast } = useApi();
  const formRef = useRef<HTMLDivElement>(null);
  const titleTemplateRef = useRef<HTMLInputElement | null>(null);
  const messageTemplateRef = useRef<HTMLTextAreaElement | null>(null);
  const [lastTemplateField, setLastTemplateField] = useState<'title' | 'message'>('message');

  const [rules, setRules] = useState<MonitoringRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resourceTypes, setResourceTypes] = useState<any[]>([]);
  const [steps, setSteps] = useState<any[]>([]);
  const [users, setUsers] = useState<{ uid: string; name: string }[]>([]);

  const [editing, setEditing] = useState<MonitoringRule | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  useEffect(() => {
    if (teamUid) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamUid]);

  const loadData = async () => {
    if (!apiContext || !teamUid) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.allSettled([
        apiContext.getMonitoringConfigs(teamUid),
        apiContext.getResourceTypes(clientUID),
        apiContext.getFlowByTeamUid(teamUid),
        apiContext.getTeamUsers(teamUid)
      ]);
      const cfgRes: any = results[0].status === 'fulfilled' ? results[0].value : null;
      const cfgData = cfgRes?.data;
      const cfgArray = Array.isArray(cfgData) ? cfgData : Array.isArray(cfgRes) ? cfgRes : [];
      setRules(cfgArray.map(safeRule));

      const rtData = results[1].status === 'fulfilled' ? (results[1].value as any)?.data : null;
      setResourceTypes(Array.isArray(rtData) ? rtData : []);

      const flowSteps = results[2].status === 'fulfilled' ? (results[2].value as any)?.data?.steps : null;
      if (Array.isArray(flowSteps)) setSteps(flowSteps);

      // Mirror TeamNotificationSettings' user normalization so we end up with
      // a flat { uid, name } list — the team-users endpoint returns mixed
      // shapes (some entries wrap user under `.user`, some flatten).
      const usersRaw = results[3].status === 'fulfilled' ? (results[3].value as any)?.data : null;
      if (Array.isArray(usersRaw)) {
        const processed = usersRaw
          .map((entry: any) => {
            let name = '';
            let uid = '';
            if (entry?.user) {
              uid = entry.user.uid;
              const fullName = `${entry.user.firstName || ''} ${entry.user.lastName || ''}`.trim();
              name = fullName || entry.user.email || entry.user.username || '';
            } else {
              uid = entry?.uid;
              const fullName = `${entry?.firstName || ''} ${entry?.lastName || ''}`.trim();
              name = fullName || entry?.name || entry?.email || entry?.username || '';
            }
            return { uid, name };
          })
          .filter((u: { uid: string; name: string }) => u.uid && u.name);
        setUsers(processed);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load monitoring rules');
    } finally {
      setLoading(false);
    }
  };

  // ==================== Actions ====================

  const handleAdd = () => {
    setEditing(emptyRule());
    setEditingIndex(null);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleEdit = (index: number) => {
    setEditing({ ...rules[index] });
    setEditingIndex(index);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleCancel = () => {
    setEditing(null);
    setEditingIndex(null);
  };

  const handleDelete = async (index: number) => {
    const rule = rules[index];
    if (rule.uid && apiContext) {
      try {
        await apiContext.deleteMonitoringConfig(teamUid, rule.uid);
        showToast('Rule deleted', 'success');
      } catch {
        showToast('Failed to delete rule', 'error');
      }
    }
    setRules(rules.filter((_, i) => i !== index));
    if (editingIndex === index) handleCancel();
  };

  const handleSave = async () => {
    if (!apiContext || !editing) return;
    if (!isFormValid()) {
      showToast('Please fix the highlighted fields', 'warning');
      return;
    }
    setSaving(true);
    try {
      let res: any;
      if (editing.uid) {
        res = await apiContext.updateMonitoringConfig(teamUid, editing.uid, editing);
      } else {
        res = await apiContext.createMonitoringConfig(teamUid, editing);
      }

      // The API returns { success, message, data } in the body. axios resolves
      // any 2xx as success, but the server may still flag a logical failure
      // with success:false on HTTP 200 (e.g. validation error paths). Treat
      // that as a real failure so users don't see a misleading success toast
      // while the rule never actually persisted.
      if (res && res.success === false) {
        const msg = res.message || res.data || 'Failed to save rule';
        showToast(typeof msg === 'string' ? msg : 'Failed to save rule', 'error');
        return;
      }

      const saved = res?.data;
      if (!saved?.uid) {
        // No persisted record came back — refuse to update local state with a
        // fake row so a refresh doesn't make the rule "disappear" silently.
        showToast('Failed to save rule (no record returned)', 'error');
        return;
      }
      const safe = safeRule(saved);

      if (editingIndex !== null) {
        const updated = [...rules];
        updated[editingIndex] = safe;
        setRules(updated);
      } else {
        setRules([...rules, safe]);
      }
      handleCancel();
      showToast('Rule saved', 'success');
    } catch (err: any) {
      const msg = err?.message || err?.data || 'Failed to save rule';
      showToast(typeof msg === 'string' ? msg : 'Failed to save rule', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ==================== Form state ====================

  const update = (patch: Partial<MonitoringRule>) => {
    if (!editing) return;
    setEditing({ ...editing, ...patch });
  };

  const updateAnchor = (patch: Partial<NonNullable<MonitoringRule['geoFenceAnchor']>>) => {
    if (!editing) return;
    setEditing({
      ...editing,
      geoFenceAnchor: { ...(editing.geoFenceAnchor || { type: 'STATIC' }), ...patch }
    });
  };

  const updateStopAnchor = (patch: Partial<NonNullable<MonitoringRule['stopAnchor']>>) => {
    if (!editing) return;
    setEditing({
      ...editing,
      stopAnchor: { ...(editing.stopAnchor || { type: 'STATIC' }), ...patch }
    });
  };

  const updateGateAnchor = (patch: Partial<NonNullable<MonitoringRule['gateAnchor']>>) => {
    if (!editing) return;
    setEditing({
      ...editing,
      gateAnchor: { ...(editing.gateAnchor || { type: 'STEP_FIELD' }), ...patch }
    });
  };

  const updateNotification = (patch: Partial<NotificationConfig>) => {
    if (!editing) return;
    setEditing({ ...editing, notification: { ...editing.notification, ...patch } });
  };

  /**
   * Insert a {{variable}} at the caret of whichever template field was last
   * focused, so clicking a chip lands the token where the admin is typing
   * rather than at the end. LOCATION_GATE has no title field — everything goes
   * to the message.
   */
  const insertTemplateVariable = (name: string) => {
    if (!editing) return;
    const target = editing.ruleType === 'LOCATION_GATE' ? 'message' : lastTemplateField;
    const el = target === 'title' ? titleTemplateRef.current : messageTemplateRef.current;
    const key = target === 'title' ? 'titleTemplate' : 'messageTemplate';
    const current = editing.notification[key] || '';
    const caret = el && typeof el.selectionStart === 'number' ? el.selectionStart : current.length;
    const token = `{{${name}}}`;
    updateNotification({ [key]: `${current.slice(0, caret)}${token}${current.slice(caret)}` });
    // Restore focus after React re-renders with the new value.
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const next = caret + token.length;
      el.setSelectionRange(next, next);
    });
  };

  const changeRuleType = (next: RuleType) => {
    if (!editing) return;
    // Reset rule-specific fields when switching types to avoid stale data.
    // Recipients/channels/severity carry over (they don't depend on the type),
    // but the alert templates do — their variables come from the old rule type
    // and would silently render empty on the new one, so drop them.
    setEditing({
      ...emptyRule(next),
      uid: editing.uid,
      name: editing.name,
      enabled: editing.enabled,
      notification: { ...editing.notification, titleTemplate: '', messageTemplate: '' },
      cooldownMinutes: editing.cooldownMinutes
    });
  };

  // ==================== Validation ====================

  const isFormValid = (): boolean => {
    if (!editing) return false;
    if (!editing.cooldownMinutes || editing.cooldownMinutes < 0) return false;
    if (editing.ruleType === 'STEP_TIMER') {
      return !!editing.startStepUid && !!editing.endStepUid && (editing.maxDurationMinutes || 0) > 0;
    }
    if (editing.ruleType === 'INTER_STEP_TRACKING') {
      return !!editing.startStepUid && !!editing.endStepUid && (editing.intervalMinutes || 0) > 0;
    }
    if (editing.ruleType === 'GEO_FENCE') {
      const a = editing.geoFenceAnchor;
      if (!a) return false;
      if (a.type === 'STATIC') {
        if (!a.coordinates || a.coordinates.length !== 2) return false;
      } else if (a.type === 'STEP_FIELD') {
        if (!a.anchorStepUid || !a.anchorFieldUid) return false;
      } else if (!a.anchorStepUid) {
        return false;
      }
      if (!((editing.geoFenceRadiusMeters || 0) > 0) || !editing.geoFenceTrackedStepUid) return false;
      if (editing.geoFenceDirection === 'RING_DWELL') {
        // An outer radius inside the inner one leaves no ring, so the rule could
        // never fire — block the save rather than let a dead config through.
        return (
          (editing.geoFenceOuterRadiusMeters || 0) > (editing.geoFenceRadiusMeters || 0) &&
          (editing.geoFenceDwellMinutes || 0) > 0
        );
      }
      return true;
    }
    if (editing.ruleType === 'MULTI_USER_PROXIMITY') {
      return (editing.proximityRadiusMeters || 0) > 0;
    }
    if (editing.ruleType === 'DRIVER_STOPPED') {
      if (!((editing.stopRadiusMeters || 0) > 0 && (editing.stopDurationMinutes || 0) > 0)) return false;
      if (editing.stopAnchorMode === 'FIXED') {
        const a = editing.stopAnchor;
        if (!a) return false;
        if (a.type === 'STATIC') {
          if (!a.coordinates || a.coordinates.length !== 2) return false;
        } else if (a.type === 'STEP_FIELD') {
          if (!a.anchorStepUid || !a.anchorFieldUid) return false;
        } else {
          return false; // stop anchor only supports STATIC / STEP_FIELD
        }
      }
      return true;
    }
    if (editing.ruleType === 'LOCATION_GATE') {
      if (!editing.gateStepUid) return false;
      const a = editing.gateAnchor;
      if (!a) return false;
      if (a.type === 'STATIC') {
        if (!a.coordinates || a.coordinates.length !== 2) return false;
      } else if (a.type === 'STEP_FIELD') {
        if (!a.anchorStepUid || !a.anchorFieldUid) return false;
      } else {
        return false; // destination only supports STATIC / STEP_FIELD
      }
      return (editing.gateRadiusMeters || 0) > 0;
    }
    return false;
  };

  // ==================== Renderers ====================

  const renderStepSelector = (label: string, value: string | undefined, onChange: (v: string) => void) => (
    <FormControl size="small" sx={{ flex: 1, minWidth: 160 }}>
      <InputLabel>{label}</InputLabel>
      <Select value={value || ''} label={label} onChange={(e) => onChange(e.target.value as string)}>
        {steps.map((s: any) => (
          <MenuItem key={s.uid} value={s.uid}>
            {s.title}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const renderFieldSelector = (
    label: string,
    stepUid: string | undefined,
    value: string | undefined,
    onChange: (v: string) => void,
  ) => {
    const step = steps.find((s: any) => s.uid === stepUid);
    const fields = step?.component?.fields || [];
    return (
      <FormControl size="small" sx={{ flex: 1, minWidth: 160 }} disabled={!stepUid}>
        <InputLabel>{label}</InputLabel>
        <Select value={value || ''} label={label} onChange={(e) => onChange(e.target.value as string)}>
          {fields.map((f: any) => (
            <MenuItem key={f.uid} value={f.uid}>
              {f.question || f.name || f.uid}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const renderRuleSpecificFields = () => {
    if (!editing) return null;
    const e = editing;

    if (e.ruleType === 'STEP_TIMER') {
      return (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={5}>
            {renderStepSelector('Start step', e.startStepUid, (v) => update({ startStepUid: v }))}
          </Grid>
          <Grid item xs={12} sm={5}>
            {renderStepSelector('End step', e.endStepUid, (v) => update({ endStepUid: v }))}
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              size="small"
              type="number"
              fullWidth
              label="Max minutes"
              value={e.maxDurationMinutes ?? ''}
              onChange={(ev) => update({ maxDurationMinutes: Number(ev.target.value) })}
            />
          </Grid>
        </Grid>
      );
    }

    if (e.ruleType === 'INTER_STEP_TRACKING') {
      return (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            {renderStepSelector('Start step', e.startStepUid, (v) => update({ startStepUid: v }))}
          </Grid>
          <Grid item xs={12} sm={4}>
            {renderStepSelector('End step', e.endStepUid, (v) => update({ endStepUid: v }))}
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              size="small"
              type="number"
              fullWidth
              label="Interval (min)"
              value={e.intervalMinutes ?? ''}
              onChange={(ev) => update({ intervalMinutes: Number(ev.target.value) })}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              size="small"
              type="number"
              fullWidth
              label="Max minutes (cap)"
              value={e.maxDurationMinutes ?? ''}
              onChange={(ev) => update({ maxDurationMinutes: Number(ev.target.value) })}
            />
          </Grid>
        </Grid>
      );
    }

    if (e.ruleType === 'GEO_FENCE') {
      const anchor = e.geoFenceAnchor || { type: 'STATIC' as AnchorType };
      return (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <FormControl size="small" fullWidth>
                <InputLabel>Anchor type</InputLabel>
                <Select value={anchor.type} label="Anchor type" onChange={(ev) => updateAnchor({ type: ev.target.value as AnchorType })}>
                  <MenuItem value="STATIC">Static coordinates</MenuItem>
                  <MenuItem value="STEP_LOCATION">Step location (GPS at submit)</MenuItem>
                  <MenuItem value="STEP_FIELD">Step field (lat/long or address)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {anchor.type === 'STATIC' ? (
              <>
                <Grid item xs={6} sm={3}>
                  <TextField
                    size="small"
                    type="number"
                    fullWidth
                    label="Latitude"
                    value={anchor.coordinates?.[0] ?? ''}
                    onChange={(ev) => updateAnchor({ coordinates: [Number(ev.target.value), anchor.coordinates?.[1] ?? 0] })}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    size="small"
                    type="number"
                    fullWidth
                    label="Longitude"
                    value={anchor.coordinates?.[1] ?? ''}
                    onChange={(ev) => updateAnchor({ coordinates: [anchor.coordinates?.[0] ?? 0, Number(ev.target.value)] })}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Label"
                    value={anchor.label || ''}
                    onChange={(ev) => updateAnchor({ label: ev.target.value })}
                  />
                </Grid>
              </>
            ) : anchor.type === 'STEP_FIELD' ? (
              <>
                <Grid item xs={12} sm={4}>
                  {renderStepSelector('Anchor step', anchor.anchorStepUid, (v) => updateAnchor({ anchorStepUid: v, anchorFieldUid: undefined }))}
                </Grid>
                <Grid item xs={12} sm={4}>
                  {renderFieldSelector('Lat/long field', anchor.anchorStepUid, anchor.anchorFieldUid, (v) => updateAnchor({ anchorFieldUid: v }))}
                </Grid>
              </>
            ) : (
              <Grid item xs={12} sm={8}>
                {renderStepSelector('Anchor step', anchor.anchorStepUid, (v) => updateAnchor({ anchorStepUid: v }))}
              </Grid>
            )}
          </Grid>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={8}>
              {renderStepSelector('Tracked step (live tracking)', e.geoFenceTrackedStepUid, (v) => update({ geoFenceTrackedStepUid: v }))}
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                size="small"
                type="number"
                fullWidth
                label={(e.geoFenceDirection || 'EXIT') === 'RING_DWELL' ? 'Inner radius (meters)' : 'Radius (meters)'}
                value={e.geoFenceRadiusMeters ?? ''}
                onChange={(ev) => update({ geoFenceRadiusMeters: Number(ev.target.value) })}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={4}>
              <FormControl size="small" fullWidth>
                <InputLabel>Trigger when</InputLabel>
                <Select
                  value={e.geoFenceDirection || 'EXIT'}
                  label="Trigger when"
                  onChange={(ev) => update({ geoFenceDirection: ev.target.value as GeoFenceDirection })}
                >
                  <MenuItem value="ENTER">Arrives within radius</MenuItem>
                  <MenuItem value="EXIT">Leaves radius (deviation)</MenuItem>
                  <MenuItem value="RING_DWELL">Stays just outside the radius for N minutes</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {(e.geoFenceDirection || 'EXIT') === 'RING_DWELL' && (
              <>
                <Grid item xs={12} sm={4}>
                  <TextField
                    size="small"
                    type="number"
                    fullWidth
                    label="Outer radius (meters)"
                    value={e.geoFenceOuterRadiusMeters ?? ''}
                    onChange={(ev) => update({ geoFenceOuterRadiusMeters: Number(ev.target.value) })}
                    error={
                      (e.geoFenceOuterRadiusMeters || 0) > 0 &&
                      (e.geoFenceOuterRadiusMeters || 0) <= (e.geoFenceRadiusMeters || 0)
                    }
                    helperText="Must be larger than the inner radius"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    size="small"
                    type="number"
                    fullWidth
                    label="Stays for (min)"
                    value={e.geoFenceDwellMinutes ?? ''}
                    onChange={(ev) => update({ geoFenceDwellMinutes: Number(ev.target.value) })}
                    helperText="2+ recommended — the sweep runs once a minute"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Fires when the driver is outside {e.geoFenceRadiusMeters || 0} m but still within{' '}
                    {e.geoFenceOuterRadiusMeters || 0} m of the anchor, continuously for{' '}
                    {e.geoFenceDwellMinutes || 0} min — drifted off-site without leaving. It stays silent for a
                    driver who leaves properly, since they cross the ring in seconds; add a separate
                    &quot;Leaves radius&quot; rule at {e.geoFenceOuterRadiusMeters || 0} m to catch that.
                  </Typography>
                </Grid>
              </>
            )}
            {(e.geoFenceDirection || 'EXIT') === 'ENTER' && (
              <Grid item xs={12} sm={8} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={e.geoFenceFireOnce !== false}
                      onChange={(ev) => update({ geoFenceFireOnce: ev.target.checked })}
                    />
                  }
                  label={
                    <Typography variant="caption">
                      Notify once on arrival (re-arms if the driver leaves and comes back)
                    </Typography>
                  }
                />
              </Grid>
            )}
          </Grid>
        </>
      );
    }

    if (e.ruleType === 'MULTI_USER_PROXIMITY') {
      const mode: ProximityMode = e.proximityMode ?? 'OUT_OF_PROXIMITY';
      const radiusLabel =
        mode === 'IN_PROXIMITY' ? 'Together radius (meters)' : 'Max pair distance (meters)';
      const radiusHelper =
        mode === 'IN_PROXIMITY'
          ? 'Fire when every active pair is within this many meters of each other.'
          : 'Fire when any active pair is more than this many meters apart.';
      return (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Trigger when</InputLabel>
                <Select
                  value={mode}
                  label="Trigger when"
                  onChange={(ev) => update({ proximityMode: ev.target.value as ProximityMode })}
                >
                  {PROXIMITY_MODE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {o.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {o.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                type="number"
                fullWidth
                label={radiusLabel}
                helperText={radiusHelper}
                value={e.proximityRadiusMeters ?? ''}
                onChange={(ev) => update({ proximityRadiusMeters: Number(ev.target.value) })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                type="number"
                fullWidth
                label="Minimum active users to evaluate"
                value={e.proximityMinUsers ?? ''}
                onChange={(ev) => update({ proximityMinUsers: Number(ev.target.value) })}
              />
            </Grid>
          </Grid>
        </>
      );
    }

    if (e.ruleType === 'DRIVER_STOPPED') {
      const mode = e.stopAnchorMode || 'SELF';
      const anchor = e.stopAnchor || { type: 'STATIC' as AnchorType };
      const anchorType: AnchorType = anchor.type === 'STEP_FIELD' ? 'STEP_FIELD' : 'STATIC';
      return (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Stop reference</InputLabel>
                <Select
                  value={mode}
                  label="Stop reference"
                  onChange={(ev) => {
                    const m = ev.target.value as 'SELF' | 'FIXED';
                    if (m === 'FIXED' && !e.stopAnchor) {
                      update({ stopAnchorMode: m, stopAnchor: { type: 'STATIC', coordinates: [0, 0] } });
                    } else {
                      update({ stopAnchorMode: m });
                    }
                  }}
                >
                  <MenuItem value="SELF">
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Anywhere (driver stops moving)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Fire when the driver stays within the radius of wherever they stop.
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="FIXED">
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Near a specific point
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Fire when the driver stays within the radius of a fixed point (static or step-field lat/long).
                      </Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {mode === 'FIXED' && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Point source</InputLabel>
                  <Select
                    value={anchorType}
                    label="Point source"
                    onChange={(ev) => updateStopAnchor({ type: ev.target.value as AnchorType })}
                  >
                    <MenuItem value="STATIC">Static coordinates</MenuItem>
                    <MenuItem value="STEP_FIELD">Step field (lat/long or address)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {anchorType === 'STEP_FIELD' ? (
                <>
                  <Grid item xs={12} sm={4}>
                    {renderStepSelector('Anchor step', anchor.anchorStepUid, (v) =>
                      updateStopAnchor({ anchorStepUid: v, anchorFieldUid: undefined }),
                    )}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    {renderFieldSelector('Lat/long field', anchor.anchorStepUid, anchor.anchorFieldUid, (v) =>
                      updateStopAnchor({ anchorFieldUid: v }),
                    )}
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      size="small"
                      type="number"
                      fullWidth
                      label="Latitude"
                      value={anchor.coordinates?.[0] ?? ''}
                      onChange={(ev) => updateStopAnchor({ coordinates: [Number(ev.target.value), anchor.coordinates?.[1] ?? 0] })}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      size="small"
                      type="number"
                      fullWidth
                      label="Longitude"
                      value={anchor.coordinates?.[1] ?? ''}
                      onChange={(ev) => updateStopAnchor({ coordinates: [anchor.coordinates?.[0] ?? 0, Number(ev.target.value)] })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Label"
                      value={anchor.label || ''}
                      onChange={(ev) => updateStopAnchor({ label: ev.target.value })}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          )}

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                type="number"
                fullWidth
                label="Radius (meters)"
                helperText={
                  mode === 'FIXED'
                    ? "Driver counts as 'stopped' while within this distance of the point."
                    : "Driver counts as 'stopped' while they stay within this many meters."
                }
                value={e.stopRadiusMeters ?? ''}
                onChange={(ev) => update({ stopRadiusMeters: Number(ev.target.value) })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                type="number"
                fullWidth
                label="Duration (minutes)"
                helperText="Fire once the driver is stationary within the radius for this long."
                value={e.stopDurationMinutes ?? ''}
                onChange={(ev) => update({ stopDurationMinutes: Number(ev.target.value) })}
              />
            </Grid>
          </Grid>
        </>
      );
    }

    if (e.ruleType === 'LOCATION_GATE') {
      const anchor = e.gateAnchor || { type: 'STEP_FIELD' as AnchorType };
      const anchorType: AnchorType = anchor.type === 'STATIC' ? 'STATIC' : 'STEP_FIELD';
      return (
        <>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={12}>
              {renderStepSelector('Step to lock (completion gated)', e.gateStepUid, (v) => update({ gateStepUid: v }))}
            </Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            The destination the driver must reach before this step can be completed.
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <FormControl size="small" fullWidth>
                <InputLabel>Destination source</InputLabel>
                <Select
                  value={anchorType}
                  label="Destination source"
                  onChange={(ev) => updateGateAnchor({ type: ev.target.value as AnchorType })}
                >
                  <MenuItem value="STEP_FIELD">Step field (lat/long or address)</MenuItem>
                  <MenuItem value="STATIC">Static coordinates</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {anchorType === 'STEP_FIELD' ? (
              <>
                <Grid item xs={12} sm={4}>
                  {renderStepSelector('Destination step', anchor.anchorStepUid, (v) =>
                    updateGateAnchor({ anchorStepUid: v, anchorFieldUid: undefined }),
                  )}
                </Grid>
                <Grid item xs={12} sm={4}>
                  {renderFieldSelector('Location field', anchor.anchorStepUid, anchor.anchorFieldUid, (v) =>
                    updateGateAnchor({ anchorFieldUid: v }),
                  )}
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={6} sm={3}>
                  <TextField
                    size="small"
                    type="number"
                    fullWidth
                    label="Latitude"
                    value={anchor.coordinates?.[0] ?? ''}
                    onChange={(ev) => updateGateAnchor({ coordinates: [Number(ev.target.value), anchor.coordinates?.[1] ?? 0] })}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    size="small"
                    type="number"
                    fullWidth
                    label="Longitude"
                    value={anchor.coordinates?.[1] ?? ''}
                    onChange={(ev) => updateGateAnchor({ coordinates: [anchor.coordinates?.[0] ?? 0, Number(ev.target.value)] })}
                  />
                </Grid>
              </>
            )}
          </Grid>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                type="number"
                fullWidth
                label="Radius (meters)"
                helperText="The driver must be within this distance of the destination to complete the step."
                value={e.gateRadiusMeters ?? ''}
                onChange={(ev) => update({ gateRadiusMeters: Number(ev.target.value) })}
              />
            </Grid>
          </Grid>
        </>
      );
    }

    return null;
  };

  // ==================== Render ====================

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const ruleTypeLabel = (t: RuleType) => RULE_TYPE_OPTIONS.find((o) => o.value === t)?.label || t;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Monitoring & Alerts
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} elevation={0} sx={{ mb: 3, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, py: 1.5 }}>
                Name
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, py: 1.5 }}>
                Rule
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, py: 1.5 }}>
                Severity
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, py: 1.5 }}>
                Channels
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, py: 1.5 }}>
                Status
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, py: 1.5 }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No monitoring rules configured
                </TableCell>
              </TableRow>
            ) : (
              rules.map((r, idx) => (
                <TableRow
                  key={r.uid || idx}
                  hover
                  sx={{ ...(editingIndex === idx && { bgcolor: 'primary.50', '&:hover': { bgcolor: 'primary.50' } }) }}
                >
                  <TableCell sx={{ fontWeight: 500 }}>
                    {r.name || (
                      <Typography component="span" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                        Unnamed
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                      <Chip size="small" variant="outlined" label={ruleTypeLabel(r.ruleType)} sx={{ fontSize: '0.7rem', height: 22 }} />
                      {r.ruleType === 'MULTI_USER_PROXIMITY' && (
                        <Chip
                          size="small"
                          label={r.proximityMode === 'IN_PROXIMITY' ? 'Together' : 'Out of proximity'}
                          sx={{
                            fontSize: '0.65rem',
                            height: 20,
                            bgcolor: r.proximityMode === 'IN_PROXIMITY' ? 'success.light' : 'warning.light',
                          }}
                        />
                      )}
                      {r.ruleType === 'DRIVER_STOPPED' && r.stopAnchorMode === 'FIXED' && (
                        <Chip
                          size="small"
                          label="Near point"
                          sx={{ fontSize: '0.65rem', height: 20, bgcolor: 'info.light' }}
                        />
                      )}
                      {/* A ring rule and a plain-EXIT rule on the same anchor are a
                          normal pairing, and would otherwise be indistinguishable here. */}
                      {r.ruleType === 'GEO_FENCE' && (
                        <Chip
                          size="small"
                          label={
                            r.geoFenceDirection === 'RING_DWELL'
                              ? `Stays ${r.geoFenceRadiusMeters ?? '?'}–${r.geoFenceOuterRadiusMeters ?? '?'} m · ${r.geoFenceDwellMinutes ?? '?'} min`
                              : r.geoFenceDirection === 'ENTER'
                                ? 'Arrives'
                                : 'Leaves'
                          }
                          sx={{
                            fontSize: '0.65rem',
                            height: 20,
                            bgcolor: r.geoFenceDirection === 'RING_DWELL' ? 'info.light' : 'warning.light'
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={r.notification.severity}
                      sx={{
                        fontSize: '0.7rem',
                        height: 22,
                        bgcolor:
                          r.notification.severity === 'CRITICAL'
                            ? 'error.light'
                            : r.notification.severity === 'WARNING'
                            ? 'warning.light'
                            : 'info.light'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(r.notification.channels || []).map((c) => (
                        <Chip key={c} size="small" label={c} sx={{ fontSize: '0.65rem', height: 20 }} />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={r.enabled ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{
                        fontWeight: 500,
                        fontSize: '0.7rem',
                        height: 22,
                        bgcolor: r.enabled ? 'success.light' : 'grey.200',
                        color: r.enabled ? 'success.dark' : 'text.secondary'
                      }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEdit(idx)} sx={{ color: 'primary.main' }}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDelete(idx)} sx={{ color: 'error.main' }}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <div ref={formRef}>
        {editing ? (
          <Card variant="outlined" sx={{ border: '1px solid', borderColor: 'primary.main' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {editingIndex !== null ? 'Edit Rule' : 'New Rule'}
                </Typography>
                <IconButton size="small" onClick={handleCancel}>
                  <Close />
                </IconButton>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
                <TextField
                  size="small"
                  label="Rule name"
                  value={editing.name}
                  onChange={(e) => update({ name: e.target.value })}
                  placeholder="e.g. Pickup-to-delivery SLA"
                  sx={{ flex: 1 }}
                />
                <FormControlLabel
                  control={<Switch checked={editing.enabled} onChange={(e) => update({ enabled: e.target.checked })} />}
                  label="Enabled"
                  sx={{ mr: 0 }}
                />
              </Box>

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Rule type</InputLabel>
                <Select value={editing.ruleType} label="Rule type" onChange={(e) => changeRuleType(e.target.value as RuleType)}>
                  {RULE_TYPE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {o.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {o.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Divider sx={{ mb: 2 }} />

              {renderRuleSpecificFields()}

              <Divider sx={{ mb: 2 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Alerting
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Severity</InputLabel>
                    <Select
                      value={editing.notification.severity}
                      label="Severity"
                      onChange={(e) => updateNotification({ severity: e.target.value as Severity })}
                    >
                      {SEVERITIES.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    size="small"
                    type="number"
                    fullWidth
                    label="Cooldown (min)"
                    value={editing.cooldownMinutes}
                    onChange={(e) => update({ cooldownMinutes: Number(e.target.value) })}
                    helperText="Suppresses repeat alerts within this window"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Channels</InputLabel>
                    <Select
                      multiple
                      value={editing.notification.channels}
                      label="Channels"
                      onChange={(e) => updateNotification({ channels: e.target.value as string[] })}
                      renderValue={(sel) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(sel as string[]).map((v) => (
                            <Chip key={v} label={v} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {CHANNELS.map((c) => (
                        <MenuItem key={c} value={c}>
                          {c}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Roles</InputLabel>
                    <Select
                      multiple
                      value={editing.notification.roles}
                      label="Roles"
                      onChange={(e) => updateNotification({ roles: e.target.value as string[] })}
                      renderValue={(sel) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(sel as string[]).map((v) => {
                            const rt = resourceTypes.find((r: any) => (r.id || r._id) === v);
                            return <Chip key={v} label={rt?.name || v} size="small" />;
                          })}
                        </Box>
                      )}
                    >
                      {resourceTypes.map((rt: any) => (
                        <MenuItem key={rt.id || rt._id} value={rt.id || rt._id}>
                          {rt.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={!!editing.notification.transactionOnly}
                        onChange={(e) => updateNotification({ transactionOnly: e.target.checked })}
                      />
                    }
                    label={
                      <Typography variant="caption">
                        Only notify users in this transaction — role recipients are limited to drivers assigned to or active on this transaction (instead of everyone in the process).
                      </Typography>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Users</InputLabel>
                    <Select
                      multiple
                      value={editing.notification.users}
                      label="Users"
                      onChange={(e) => updateNotification({ users: e.target.value as string[] })}
                      renderValue={(sel) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(sel as string[]).map((v) => {
                            const u = users.find((x) => x.uid === v);
                            return <Chip key={v} label={u?.name || v} size="small" />;
                          })}
                        </Box>
                      )}
                    >
                      {users.length === 0 ? (
                        <MenuItem disabled>
                          <em>No team users found</em>
                        </MenuItem>
                      ) : (
                        users.map((u) => (
                          <MenuItem key={u.uid} value={u.uid}>
                            {u.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Divider sx={{ mb: 2 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Alert text
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                {editing.ruleType === 'LOCATION_GATE'
                  ? 'What the driver is told when this rule blocks the step. Leave blank to use the default wording.'
                  : 'What this rule’s alerts say. Leave blank to use the default wording. Click a variable to insert it at the cursor.'}
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                {templateVarsFor(editing.ruleType).map((v) => (
                  <Tooltip key={v.name} title={v.description}>
                    <Chip
                      label={`{{${v.name}}}`}
                      size="small"
                      variant="outlined"
                      onClick={() => insertTemplateVariable(v.name)}
                      sx={{ fontFamily: 'monospace', fontSize: 11, cursor: 'pointer' }}
                    />
                  </Tooltip>
                ))}
              </Box>

              <Grid container spacing={2} sx={{ mb: 1 }}>
                {editing.ruleType !== 'LOCATION_GATE' && (
                  <Grid item xs={12}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Alert title"
                      placeholder="Default: the built-in title for this rule type"
                      inputRef={titleTemplateRef}
                      value={editing.notification.titleTemplate || ''}
                      onFocus={() => setLastTemplateField('title')}
                      onChange={(e) => updateNotification({ titleTemplate: e.target.value })}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    label={editing.ruleType === 'LOCATION_GATE' ? 'Blocked message' : 'Alert message'}
                    placeholder="Default: the built-in message for this rule type"
                    inputRef={messageTemplateRef}
                    value={editing.notification.messageTemplate || ''}
                    onFocus={() => setLastTemplateField('message')}
                    onChange={(e) => updateNotification({ messageTemplate: e.target.value })}
                  />
                </Grid>
              </Grid>

              {(editing.notification.titleTemplate || editing.notification.messageTemplate) && (
                <Box
                  sx={{
                    p: 1.5,
                    mb: 2,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Preview (sample values)
                  </Typography>
                  {editing.ruleType !== 'LOCATION_GATE' && editing.notification.titleTemplate && (
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {renderPreview(editing.notification.titleTemplate)}
                    </Typography>
                  )}
                  {editing.notification.messageTemplate && (
                    <Typography variant="body2">{renderPreview(editing.notification.messageTemplate)}</Typography>
                  )}
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 1 }}>
                <Button variant="outlined" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving || !isFormValid()}>
                  {saving ? 'Saving...' : editingIndex !== null ? 'Update Rule' : 'Save Rule'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
            Add Rule
          </Button>
        )}
      </div>
    </Box>
  );
};

export default MonitoringSettings;
